import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Box } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import QuickAddSheet from './QuickAddSheet';
import MoreSheet from './MoreSheet';
import PhoneMovementSheet from './PhoneMovementSheet';
import { OPEN_MOVEMENT_EVENT } from './events';

const noop = () => {};
// What a page gets from usePhoneShell() outside the phone shell (the desktop),
// so it can call it unconditionally.
const DESKTOP_SHELL = {
  openQuickAdd: noop,
  openMore: noop,
  openMovement: noop,
  closeMovement: noop,
  refreshSignal: 0,
  bumpRefresh: noop,
};

const TOP_BAR_KEYS = ['title', 'left', 'right', 'centered', 'hidden', 'scrolled'];
const sameTopBar = (a, b) => Boolean(a && b) && TOP_BAR_KEYS.every((k) => Object.is(a[k], b[k]));

const ShellContext = createContext(null);
const TopBarContext = createContext(null);
const TopBarSetterContext = createContext(null);

/**
 * The phone shell's shared state: the sheets every page can open (quick add,
 * More, a movement) and the top bar each page declares.
 *
 * Quick add is always mounted and opened imperatively, because iOS raises
 * the keyboard only for a focus() made inside the user's tap: by the time a
 * state change had rendered a new sheet, the tap would be over.
 *
 * `refreshSignal` goes up after anything on the phone saves, fixes or
 * deletes a movement; lists and counts depend on it to refetch.
 */
export function PhoneShellProvider({ children, onOpenSettings }) {
  const { can, ready } = useWorkspace();
  const canCreate = can('movement.create');
  const navigate = useNavigate();
  const location = useLocation();

  const contentRef = useRef(null);
  const quickAddRef = useRef(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [movement, setMovement] = useState({ id: null, isOpen: false });
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [topBar, setTopBar] = useState({ token: null, config: null });

  const openQuickAdd = useCallback((options, openOptions) => {
    quickAddRef.current?.open(options, openOptions);
  }, []);
  const openMore = useCallback(() => setMoreOpen(true), []);
  const openMovement = useCallback((id) => setMovement({ id, isOpen: true }), []);
  const closeMovement = useCallback(() => setMovement((m) => ({ ...m, isOpen: false })), []);
  const bumpRefresh = useCallback(() => setRefreshSignal((n) => n + 1), []);

  useEffect(() => {
    const onOpen = (event) => openMovement(event.detail.id);
    window.addEventListener(OPEN_MOVEMENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_MOVEMENT_EVENT, onOpen);
  }, [openMovement]);

  // `?new=1` (a home-screen shortcut) opens quick add, then leaves the URL,
  // for anyone: a role that cannot create still gets a clean URL. Nothing is
  // focused: outside a tap iOS would not raise the keypad anyway, so the user
  // taps the amount.
  const handledNewKey = useRef(null);
  useEffect(() => {
    if (!ready) return;
    const params = new URLSearchParams(location.search);
    if (!params.has('new')) return;
    const opens = params.get('new') === '1' && canCreate && handledNewKey.current !== location.key;
    handledNewKey.current = location.key;
    params.delete('new');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    if (opens) quickAddRef.current?.open({}, { focus: false });
  }, [ready, canCreate, location.key, location.pathname, location.search, navigate]);

  const shell = useMemo(() => ({
    openQuickAdd, openMore, openMovement, closeMovement, refreshSignal, bumpRefresh,
  }), [openQuickAdd, openMore, openMovement, closeMovement, refreshSignal, bumpRefresh]);

  const setter = useMemo(() => ({
    set: (token, config) => setTopBar({ token, config }),
    // Only the page that holds the bar updates it, and only when it changed.
    update: (token, config) => setTopBar((prev) => (
      prev.token !== token || sameTopBar(prev.config, config) ? prev : { token, config }
    )),
    clear: (token) => setTopBar((prev) => (prev.token === token ? { token: null, config: null } : prev)),
  }), []);

  return (
    <ShellContext.Provider value={shell}>
      <TopBarSetterContext.Provider value={setter}>
        <TopBarContext.Provider value={topBar.config}>
          {/* Quick add makes everything in here inert while it is open. */}
          <Box ref={contentRef}>{children}</Box>
          {canCreate && <QuickAddSheet ref={quickAddRef} contentRef={contentRef} />}
          <MoreSheet
            isOpen={moreOpen}
            onClose={() => setMoreOpen(false)}
            onOpenSettings={onOpenSettings}
          />
          <PhoneMovementSheet
            movementId={movement.id}
            isOpen={movement.isOpen}
            onClose={closeMovement}
            onChanged={bumpRefresh}
          />
        </TopBarContext.Provider>
      </TopBarSetterContext.Provider>
    </ShellContext.Provider>
  );
}

/**
 * `{ openQuickAdd, openMore, openMovement, closeMovement, refreshSignal,
 * bumpRefresh }`. Outside the phone shell every action does nothing, so a
 * shared component can call it on any width.
 */
export function usePhoneShell() {
  return useContext(ShellContext) || DESKTOP_SHELL;
}

/** The top bar config the current page declared, or null. */
export function usePhoneTopBarConfig() {
  return useContext(TopBarContext);
}

/**
 * A page declares its top bar: `{ title, left?, right?, centered?, hidden?,
 * scrolled? }`. `scrolled` shows the hairline under the bar; left out, the bar
 * works it out from the page's scroll.
 * It stays until the page unmounts. Without `deps` it is re-read after every
 * render of the page, so a `left` or `right` that depends on state never goes
 * stale; with `deps`, only when they change. Does nothing on the desktop.
 */
export function usePhoneTopBar(config, deps) {
  const setter = useContext(TopBarSetterContext);
  const latest = useRef(config);
  latest.current = config;
  const tokenRef = useRef(null);

  useLayoutEffect(() => {
    if (!setter) return undefined;
    const token = {};
    tokenRef.current = token;
    setter.set(token, latest.current);
    return () => {
      tokenRef.current = null;
      setter.clear(token);
    };
  }, [setter]);

  useLayoutEffect(() => {
    if (setter && tokenRef.current) setter.update(tokenRef.current, latest.current);
  // No `deps` means every render; the caller's `deps` otherwise.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
